import { bumpActivity } from './chatStore.activity'
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import * as Sentry from '@sentry/react'
import { useAuthStore } from '../shared/stores/auth'
import { useVoiceStore } from './voiceStore'
import { sendStandardMessage } from './sendStandardMessage'
import type { RichContent, SuggestedAction } from '../types/rich-content'
import type { ActionCard } from '../lib/parse-action-cards'
import { stripSyntheticRailTurns } from '../lib/synthetic-rail-turn'
import { mesclarHistoricoDoServidor } from '../lib/chat/historico-sem-duplicata'
import { invalidateConversationList } from '../lib/request-dedupe'
import { isOrphanedImportNotice } from './chatStore.orphan-notices'
import { createRenameConversation, type RenameConversationResult } from './chatStore.rename'
import {
  inactivityNoticeForDisk,
  resumeAfterInactivity,
  type InactivityNotice,
} from './chatStore.inactivity'
import {
  CHAT_STORAGE_NAME,
  createInitialChatState,
  identityScopedStorage,
} from './chatStore.identity'

export {
  chatStorageKeyFor,
  createInitialChatState,
  syncChatStoreIdentity,
} from './chatStore.identity'

export interface MessageAttachment {
  filename: string
  fileType: 'xlsx' | 'pdf' | 'docx' | 'img' | 'csv' | 'txt'
  sizeBytes: number
}

export interface WebSearchMeta {
  searched: boolean
  provider: string
  source_count: number
  cache_hit: boolean
  sources: Array<{ title: string; url: string; confidence: number; domain: string }>
  equipment: { manufacturer: string; model: string } | null
}

export interface Message {
  id: string
  conversationId: string
  content: string
  role: 'user' | 'assistant'
  timestamp: number
  status: 'sending' | 'sent' | 'error' | 'cancelled'
  richContent?: RichContent
  webSearchMeta?: WebSearchMeta
  suggestedActions?: SuggestedAction[]
  attachments?: MessageAttachment[]
  /**
   * Optional friendly label shown in the user bubble. When set, the UI
   * renders this instead of `content`. `content` still travels to the
   * engine over the wire. Used by action cards (Import / Confirm) that
   * need to dispatch a technical instruction ("Confirmo a importação
   * da proposta {uuid}...") while showing the user a plain-English
   * action label ("Importar com este mapeamento"). Only meaningful
   * for `role: 'user'` messages.
   */
  displayText?: string
  /**
   * When `true`, the assistant message is being streamed token-by-token
   * from the engine. The UI renders the partial content with a typewriter
   * cursor and skips the final-state polish (rich content cards, etc).
   * Cleared by `finalizeStreamingMessage` once the `response` SSE event
   * arrives (or by the error handler if the stream is interrupted).
   */
  isStreaming?: boolean
  /**
   * Sprint 1A — propositional action cards extracted from the engine response.
   * The engine sends these in `metadata.actionCards`. The frontend parser
   * is used as fallback if the inline `<ACTION_CARDS>` block is present.
   * Null when the message is not conclusive or no valid cards were produced.
   */
  metadata?: {
    actionCards?: ActionCard[]
    clientRequestId?: string
  }
  /**
   * Paleta de comandos do campo de digitar (etapa 1, R10). Quando a
   * mensagem nasceu de um comando escolhido na barra ("/equipamento"), este
   * campo carrega o id do comando ("equipamento") e o balão do usuário
   * mostra uma etiqueta pequena `/<id>` (`MessageBubbleUser`). `undefined`
   * para toda mensagem de texto livre.
   */
  commandId?: string
}

export type { RenameConversationResult }

export interface Conversation {
  id: string
  title: string
  createdAt: number
  updatedAt: number
}

/** STO-1150 — bolha de streaming em voo é transitória e NUNCA vai ao disco. */
function persisteParaODisco(message: Message): boolean {
  return (
    message.isStreaming !== true && message.status !== 'error' && !isOrphanedImportNotice(message)
  )
}

export type ProgressStep =
  | 'searching'
  | 'sources'
  | 'analyzing'
  | 'tools'
  | 'generating'
  | 'reasoning'

export interface ChatState {
  conversations: Conversation[]
  activeConversationId: string | null
  messages: Message[]
  isTyping: boolean
  responseStartedAt?: number | null
  pendingConversation: boolean
  progressStep: ProgressStep | null
  progressDetail: string | null
  /**
   * JilvupBs — definitive stream-failure signal for the LIVE reasoning
   * indicator. Holds an i18n key (`errors.*`) or a literal display string when
   * the in-flight turn errored. Drives the indicator's inline error+retry
   * surface so it never silently vanishes on failure. Distinct from the
   * per-message `status: 'error'` bubble: this flag lets the indicator render
   * an error state even when no streaming bubble was ever created (error before
   * first chunk). Cleared when a fresh turn starts (`setProgress` / new send)
   * or on explicit retry — NOT by `clearProgress` (the error must outlive the
   * progress reset that accompanies it).
   */
  streamError: string | null
  inactivityNotice: InactivityNotice | null
  inactivityResuming: boolean
  inputDraft: string
  /**
   * Sinal efêmero de "um comando novo foi ACEITO para despacho" nesta conversa
   * (voz, sugestão, botão e texto passam todos pelo mesmo `sendRaw`). Quem
   * rola a lista (`FollowOnNewTurn`) rearma o "seguir o fim" quando o `seq`
   * muda. Fora do disco (não está no `partialize`) e fora do reset de
   * identidade de propósito: é evento, não dado — e `seq` só cresce.
   */
  newTurnSignal?: { conversationId: string; seq: number } | null

  setActiveConversation: (id: string | null) => void
  setInputDraft: (text: string) => void
  /** Anuncia que um comando novo foi aceito (ver `newTurnSignal`). */
  signalNewTurn: (conversationId: string) => void
  addMessage: (message: Message) => void
  updateMessageStatus: (id: string, status: Message['status']) => void
  appendChunkToMessage: (id: string, chunk: string) => void
  finalizeStreamingMessage: (id: string, updates: Partial<Omit<Message, 'id'>>) => void
  mergeStreamingMessageCards: (id: string | null, cards: Partial<RichContent>) => void
  setStreamingMessageProcessing: (id: string, processing: string | null) => void
  setIsTyping: (isTyping: boolean) => void
  setProgress: (step: ProgressStep, detail: string) => void
  clearProgress: () => void
  setStreamError: (message: string) => void
  clearStreamError: () => void
  setInactivityNotice: (notice: InactivityNotice) => void
  clearInactivityNotice: () => void
  resumeAfterInactivity: () => void
  addConversation: (conversation: Conversation) => void
  setConversations: (conversations: Conversation[]) => void
  setMessages: (messages: Message[]) => void
  setPendingConversation: (pending: boolean) => void
  removeConversation: (id: string) => void
  updateConversation: (id: string, updates: Partial<Omit<Conversation, 'id'>>) => void
  /**
   * T3 — renomear conversa. UMA função para o cabeçalho e a sidebar. Otimista:
   * a tela muda na hora e volta ao nome anterior (com aviso) se o motor falhar.
   * Vazio ou > 120 caracteres não salva nada e devolve `INVALID_TITLE`.
   */
  renameConversation: (id: string, rawTitle: string) => Promise<RenameConversationResult>
  mergeMessages: (newMessages: Message[]) => void
  /**
   * Funde o histórico do servidor SEM duplicar o turno que a pessoa acabou de
   * viver. É o caminho da recarga (`ChatPage`), e existe separado do
   * `mergeMessages` porque a regra é outra: `mergeMessages` só sabe casar por
   * id, e o id do turno vivo nasce no NAVEGADOR enquanto o do histórico vem do
   * BANCO — por isso a conversa recarregada aparecia duas vezes. A régua e a
   * medição estão em `../lib/chat/historico-sem-duplicata.ts`.
   */
  ingestConversationHistory: (conversationId: string, doServidor: Message[]) => void
  /**
   * Remove a single message by id (also purges it from persisted storage).
   * Used to drop a stale `import_job_summary` card whose import job no longer
   * exists on the engine (404), so it never resurfaces on a new conversation.
   */
  removeMessage: (id: string) => void
  /**
   * Sprint 1A — sends a standardised message from an ActionCard click.
   * Reuses the existing sendRaw flow via ConnectionStore but marks
   * the outgoing user message with `source: 'action_card'` for PostHog
   * analytics tracking. Call this from ActionCardList click handlers.
   */
  sendStandardMessage: (
    text: string,
    source?: string,
    messageId?: string,
    clickLabel?: string,
  ) => Promise<boolean>
}

export function selectTurnInFlight(state: Pick<ChatState, 'isTyping' | 'messages'>): boolean {
  return state.isTyping || state.messages.some((message) => message.isStreaming === true)
}

/** Limite só do retrato em disco; a sessão mantém todas as mensagens em memória. */
export const TETO_DO_CACHE_NO_DISCO = 400

/**
 * State patch for `setPendingConversation`. Independent review 2026-07-23
 * (achado 2): `streamError` is global and was only cleared INSIDE a send
 * (`connectionStore.sendRaw` / `sendStandardMessage`), both of which run AFTER
 * ChatPage's onboarding kickoff effect. A turn that failed left the error
 * standing, so on the next "Nova Conversa" the waiting gate fell in the
 * pre-effect frame and the wrong hero screen flashed once — the exact symptom
 * this work exists to kill. Arming an episode is the single seam BOTH entry
 * points share (Sidebar's button and ChatPage's hub CTA), so the clear lives
 * here instead of being duplicated at each call site. Declared at module scope
 * so the store creator does not grow past the modularity ratchet.
 */
/**
 * O BOTÃO "PARAR" ACESO NUMA CONVERSA PARADA — Denis, 29/08/2026, 21:11 em
 * homologação.
 *
 * O DEFEITO, em português de manutenção: um turno da conversa anterior não
 * recebeu o evento de fim (queda da conexão, aba em segundo plano, o
 * cão-de-guarda cortando). A marca "está respondendo" (`isTyping`) é UMA SÓ
 * para o aplicativo inteiro, e trocar de conversa não a apagava. A conversa
 * seguinte nascia herdando a marca da anterior e desenhava "Parar" sem nada
 * rodando — como tirar um caminhão da garagem e o painel continuar mostrando o
 * defeito do caminhão anterior.
 *
 * A REGRA QUE FICA: a marca é POR CONVERSA. SAIR de uma conversa apaga a marca.
 *
 * ── POR QUE `anterior !== null` E NÃO SÓ `anterior !== novo` ────────────────
 *
 * Esta é a metade que impede o conserto de virar defeito, e ela é medida, não
 * suposta. Numa conversa NOVA o envio começa ANTES de a conversa existir: o
 * turno já está em curso quando a rota resolve o id e `ChatPage` chama esta
 * função com `null -> id` (`ChatPage.tsx`, o efeito que segue `useParams`).
 * Se a limpeza olhasse só "mudou de id", ela apagaria a marca DO TURNO QUE
 * ACABOU DE COMEÇAR, e o usuário perderia o indicador no meio da primeira
 * resposta — trocando um botão presete por um indicador que some.
 *
 * `null` não é uma conversa da qual se possa SAIR: é "nenhuma em foco ainda".
 * Só a saída de uma conversa REAL limpa.
 */
function saiuDeUmaConversaDeVerdade(anterior: string | null, novo: string | null): boolean {
  return anterior !== null && anterior !== novo
}

export function pendingConversationPatch(pending: boolean): {
  pendingConversation: boolean
  streamError?: null
  isTyping?: false
  progressStep?: null
  progressDetail?: null
} {
  // "Nova conversa" é o OUTRO caminho de entrada numa conversa (o primeiro é
  // `setActiveConversation`), e precisa da mesma limpeza pela mesma razão: o
  // botão "Parar" aceso sem stream que o Denis viu em 29/08. Sem esta metade,
  // clicar "Nova conversa" logo depois de um turno que morreu sem evento de
  // fim abriria a conversa nova já com "Parar" aceso.
  return pending
    ? {
        pendingConversation: true,
        streamError: null,
        isTyping: false,
        progressStep: null,
        progressDetail: null,
      }
    : { pendingConversation: false }
}

// Contador do `newTurnSignal`. Módulo, não estado: o reset de identidade não o
// zera, então um `seq` nunca se repete e nenhum envio passa despercebido.
let newTurnSignalSeq = 0

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      ...createInitialChatState(),

      setActiveConversation: (id) => {
        set((state) => {
          // Saiu de uma conversa REAL? Então a marca de "está respondendo" era
          // dela e não acompanha o usuário — ver o doc de
          // `saiuDeUmaConversaDeVerdade`.
          const limpaMarcaDeResposta = saiuDeUmaConversaDeVerdade(state.activeConversationId, id)
          return {
            activeConversationId: id,
            // Drop orphaned import notices (see isOrphanedImportNotice) the
            // moment the user leaves the empty-conversation session they were
            // surfaced in, so they can't resurface on the NEXT "Nova conversa".
            messages: state.messages.filter((m) => !isOrphanedImportNotice(m)),
            isTyping: limpaMarcaDeResposta ? false : state.isTyping,
            progressStep: limpaMarcaDeResposta ? null : state.progressStep,
            progressDetail: limpaMarcaDeResposta ? null : state.progressDetail,
          }
        })
        // Anti-leak (spec 2026-07-11 §5): a handshake parked for one
        // conversation must never resurface (card or "sim"/"não" intercept)
        // after the user switches to a different conversation.
        useVoiceStore.getState().clearPendingConfirmation()
      },
      setInputDraft: (text) => set({ inputDraft: text }),
      signalNewTurn: (conversationId) =>
        set({ newTurnSignal: { conversationId, seq: ++newTurnSignalSeq } }),
      addMessage: (message) => {
        // Id dedup guard: if a message with this id already exists, REPLACE it
        // in place rather than appending a duplicate. This makes sticky-id
        // inserts (e.g. the per-turn error bubble in connectionStore.onError)
        // idempotent on re-send — a second errored send overwrites the first
        // bubble instead of stacking a second one.
        set((state) => {
          const idx = state.messages.findIndex((m) => m.id === message.id)
          if (idx !== -1) {
            const next = state.messages.slice()
            next[idx] = message
            return { messages: next }
          }
          const conversations = bumpActivity(state.conversations, message)
          return { messages: [...state.messages, message], conversations }
        })
      },
      updateMessageStatus: (id, status) =>
        set((state) => ({
          messages: state.messages.map((m) => (m.id === id ? { ...m, status } : m)),
        })),
      appendChunkToMessage: (id, chunk) =>
        set((state) => ({
          messages: state.messages.map((m) =>
            m.id === id ? { ...m, content: m.content + chunk } : m,
          ),
        })),
      finalizeStreamingMessage: (id, updates) =>
        set((state) => ({
          messages: state.messages.map((m) =>
            m.id === id ? { ...m, ...updates, isStreaming: false } : m,
          ),
        })),
      mergeStreamingMessageCards: (id, cards) =>
        set((state) => ({
          messages: state.messages.map((m) =>
            m.id === id && m.isStreaming
              ? { ...m, richContent: { ...m.richContent, ...cards } }
              : m,
          ),
        })),
      setStreamingMessageProcessing: (id, processing) =>
        set((state) => ({
          messages: state.messages.map((m) =>
            m.id === id
              ? { ...m, richContent: { ...m.richContent, processing: processing ?? undefined } }
              : m,
          ),
        })),
      setIsTyping: (isTyping) =>
        set((state) => ({
          isTyping,
          responseStartedAt: isTyping
            ? state.isTyping
              ? state.responseStartedAt
              : Date.now()
            : null,
        })),
      // A fresh progress event means the engine is working again — any stale
      // stream error from a previous attempt no longer applies, so clear it.
      setProgress: (step, detail) =>
        set({ progressStep: step, progressDetail: detail, streamError: null }),
      // Intentionally does NOT touch streamError: the error path calls
      // clearProgress() right after the failure, and the indicator must keep
      // showing the error+retry surface instead of silently emptying.
      clearProgress: () => set({ progressStep: null, progressDetail: null }),
      setStreamError: (message) => set({ streamError: message }),
      clearStreamError: () => set({ streamError: null }),
      setInactivityNotice: (notice) => set({ inactivityNotice: notice }),
      clearInactivityNotice: () => set({ inactivityNotice: null }),
      resumeAfterInactivity: () => resumeAfterInactivity(get, set),
      addConversation: (conversation) => {
        Sentry.addBreadcrumb({
          category: 'tenant',
          message: 'conversation_created',
          data: {
            conversationId: conversation.id,
            tenantId: useAuthStore.getState().tenantId,
          },
        })
        invalidateConversationList()
        set((state) => ({ conversations: [conversation, ...state.conversations] }))
      },
      setConversations: (conversations) => set({ conversations }),
      setMessages: (messages) => set({ messages }),
      setPendingConversation: (pending) => set(pendingConversationPatch(pending)),
      removeConversation: (id) => {
        invalidateConversationList()
        set((state) => ({
          conversations: state.conversations.filter((c) => c.id !== id),
          messages: state.messages.filter((m) => m.conversationId !== id),
          activeConversationId:
            state.activeConversationId === id ? null : state.activeConversationId,
          pendingConversation:
            state.activeConversationId === id ? false : state.pendingConversation,
        }))
      },
      updateConversation: (id, updates) => {
        invalidateConversationList()
        set((state) => ({
          conversations: state.conversations.map((c) => (c.id === id ? { ...c, ...updates } : c)),
        }))
      },
      renameConversation: createRenameConversation(set, get),
      mergeMessages: (newMessages) =>
        set((state) => {
          const existingIds = new Set(state.messages.map((m) => m.id))
          const toAdd = newMessages.filter((m) => !existingIds.has(m.id))
          return toAdd.length > 0 ? { messages: [...state.messages, ...toAdd] } : state
        }),
      ingestConversationHistory: (conversationId, doServidor) =>
        set((state) => {
          if (doServidor.length === 0) return state
          const proximas = mesclarHistoricoDoServidor(state.messages, doServidor, conversationId)
          return { messages: proximas }
        }),
      removeMessage: (id) =>
        set((state) => {
          const next = state.messages.filter((m) => m.id !== id)
          return next.length === state.messages.length ? state : { messages: next }
        }),
      sendStandardMessage: (text, source = 'action_card', stableMessageId, clickLabel) =>
        sendStandardMessage(get, text, source, stableMessageId, clickLabel),
    }),
    {
      name: CHAT_STORAGE_NAME,
      // SOC-011 — o adaptador ignora o `name` acima e calcula a própria chave
      // por identidade a cada chamada (ver `identityScopedStorage`).
      storage: createJSONStorage(() => identityScopedStorage),
      // SOC-011 — sem isto a hidratação roda no import do módulo, antes de
      // `syncClerkState` existir. `syncChatStoreIdentity` chama
      // `persist.rehydrate()` explicitamente quando a identidade é conhecida.
      skipHydration: true,
      // v2 — remove erros transitórios e avisos órfãos do retrato em disco.
      // v3 — also purge the engine's synthetic rail-click user turns
      // ("Ação da esteira: <id>."): browsers that saw the ghost-bubble bug
      // before the fix have them persisted, and the history-ingestion strip
      // (ChatPage) never runs for a non-empty cache — left in, they keep
      // feeding the sidebar-title derivation and retryLastUserMessage.
      // v4 — remove bolhas em streaming para não duplicar após F5.
      version: 5,
      migrate: (persisted) => {
        const state = persisted as { messages?: Message[] } | undefined
        if (state?.messages) {
          state.messages = stripSyntheticRailTurns(state.messages.filter(persisteParaODisco))
        }
        return state as ChatState
      },
      partialize: (state) => ({
        conversations: state.conversations,
        activeConversationId: state.activeConversationId,
        // A poda fica AQUI, no que vai para o disco, e não no que a sessão
        // guarda: rolar uma conversa longa continua funcionando, e o retrato
        // gravado para de crescer sem fim. `slice(-N)` mantém as RECENTES —
        // perder a última mensagem seria o pior caso possível, porque é
        // justamente o turno que motivou o recarregamento.
        messages: stripSyntheticRailTurns(state.messages.filter(persisteParaODisco)).slice(
          -TETO_DO_CACHE_NO_DISCO,
        ),
        inactivityNotice: inactivityNoticeForDisk(state.inactivityNotice),
      }),
    },
  ),
)
